import styles from './styles.module.scss';
import { FilterSection } from './FilterSection';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { DatePicker } from 'components';
import { Locations, Checkbox } from 'components';
import { Input } from 'components-antd';
import { Location } from 'types';
import { SearchIcon } from '../Icons/SearchIcon';
import classNames from 'classnames';
import { useFormik } from 'formik';
import moment from 'moment/moment';
import { Autocomplete } from '../Autocomplete';
import { AgentIntelFiltersType, FeedVisibility } from 'types/intel';
import { useSelector } from 'react-redux';
import { getUserId } from 'store/selectors/user';
import { TEAM_ADMIN } from 'settings/constants/roles';
import { getAgentTeamRoleSelector } from 'store/selectors/agentTeamDetail';
import { debounce } from 'lodash-es';

type FiltersPropsType = {
  systemTags: any[];
  teamMembers: any[];
  filters: AgentIntelFiltersType;
  setFilters: Function;
  masterVisibilitySwitch: FeedVisibility;
  activeFilterSection: string | null;
  setActiveFilterSection: Function;
};

export const Filters = (props: FiltersPropsType) => {
  const {
    systemTags,
    teamMembers,
    filters,
    setFilters,
    masterVisibilitySwitch,
    activeFilterSection,
    setActiveFilterSection,
  } = props;
  const currentUserId = useSelector(getUserId);
  const agentRole = useSelector(getAgentTeamRoleSelector);
  const isFirstRender = useRef<boolean>(true);

  const managedTeamMembers =
    agentRole === TEAM_ADMIN
      ? teamMembers.filter((member) => member.IsManagedByCurrentUser)
      : teamMembers;

  const currentUser = teamMembers.find((item) => item.id === currentUserId);

  const formik = useFormik({
    initialValues: filters,
    onSubmit: (values) => {
      // Do nothing
    },
  });

  const debouncedSetFilters = useRef(
    debounce((values) => {
      setFilters(values);
    }, 500),
  ).current;

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    debouncedSetFilters(formik.values);
  }, [formik.values]);

  const activeTagsCount = useMemo(
    () => formik.values.tags.filter((tag) => tag.active).length,
    [formik.values.tags],
  );

  const generateLocationName = (location: Location) => {
    if (location.PlaceName && location.PlaceName.endsWith(', USA')) {
      return location.PlaceName.slice(0, location.PlaceName.lastIndexOf(', USA'));
    }

    return `${location.City}, ${location.State}`;
  };

  const handleTagSelection = (val) => {
    const found = systemTags.find((item) => item.Id === val);

    const obj = {
      value: val,
      label: found?.Title ?? '-',
      active: true,
    };

    const newTags = [...formik.values.tags, obj];
    formik.setFieldValue('tags', newTags);
  };

  const handleAgentSelection = (val) => {
    const found = teamMembers.find((item) => item.id === val);

    const obj = {
      value: val,
      label: found?.name ?? '-',
      active: true,
    };

    const newTags = [...formik.values.agents, obj];
    formik.setFieldValue('agents', newTags);
  };

  return (
    <div className={styles.filterContainer}>
      <FilterSection
        title="Keywords"
        activeKey={activeFilterSection}
        setActiveSection={setActiveFilterSection}
      >
        <Input
          className={styles.input}
          hideOutline
          placeholder="Search by keyword ..."
          prefix={<SearchIcon />}
          value={formik.values.keyword}
          onChange={(event) => {
            formik.setFieldValue('keyword', event.target.value);
          }}
        />
      </FilterSection>
      <div className={styles.divider} />
      <FilterSection
        title="Areas"
        activeKey={activeFilterSection}
        setActiveSection={setActiveFilterSection}
      >
        <div className={styles.locationWrapper}>
          <Locations
            multiple
            shouldFilterPlaces
            showOnlyCityAndState
            allowStates
            getStates
            variant={Locations.SQUARE}
            searchIcon
            searchIconClassName={styles.searchIcon}
            name="locations"
            onResult={(result, preparedData: Location[]) => {
              formik.setFieldValue('locations', [
                ...formik.values.locations.map((l) => {
                  return l.active ||
                    preparedData.findIndex(
                      (location) => location.ProviderPlaceId === l.location.ProviderPlaceId,
                    ) === -1
                    ? l
                    : { ...l, active: true };
                }),
                ...preparedData
                  .filter(
                    (location) =>
                      !formik.values.locations.some(
                        (l) => l.location.ProviderPlaceId === location.ProviderPlaceId,
                      ),
                  )
                  .map((location) => ({
                    location,
                    active: true,
                  })),
              ]);
            }}
            value={[]}
            placeholder="Search by area ..."
            activeInputClassName={styles.activeInputClassName}
            placeholderClassName={styles.placeholderClass}
          />
        </div>

        {formik.values.locations.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              width: '100%',
            }}
          >
            {formik.values.locations.map(({ location, active }, index) => (
              <Checkbox
                key={location.ProviderPlaceId}
                className={styles.checkbox}
                labelClassName={styles.checkboxLabel}
                labelTextClassName={styles.checkboxLabelText}
                label={generateLocationName(location)}
                checked={active}
                onChange={() => {
                  formik.setFieldValue(
                    'locations',
                    [...formik.values.locations].map((l, i) =>
                      i === index ? { ...l, active: !l.active } : l,
                    ),
                  );
                }}
                closeIcon
                onClickClose={() => {
                  formik.setFieldValue(
                    'locations',
                    [...formik.values.locations].filter((l, i) => i !== index),
                  );
                }}
                closeIconClassName={styles.closeIcon}
                direction={Checkbox.DIRECTION_RIGHT}
              />
            ))}
          </div>
        )}
      </FilterSection>
      <div className={styles.divider} />
      <FilterSection
        title="Date"
        activeKey={activeFilterSection}
        setActiveSection={setActiveFilterSection}
      >
        <div className={styles.dateRange}>
          <DatePicker
            format="m/d/Y"
            placeholder="From"
            inputInnerClass={styles.datePicker}
            icon
            variant={DatePicker.ROUNDED}
            allowPlugin={false}
            name="dateRange.from"
            value={formik.values.dateRange.from}
            onChange={(event) => {
              formik.setFieldValue(event.target.name, event.target.value[0]);
            }}
            options={{
              enableTime: false,
              maxDate: formik.values.dateRange.to ?? moment().toDate(),
            }}
          />
          <div className={styles.to}>to</div>
          <DatePicker
            format="m/d/Y"
            placeholder="To"
            inputInnerClass={styles.datePicker}
            icon
            variant={DatePicker.ROUNDED}
            allowPlugin={false}
            name="dateRange.to"
            value={formik.values.dateRange.to}
            onChange={(event) => {
              formik.setFieldValue(event.target.name, event.target.value[0]);
            }}
            options={{
              enableTime: false,
              maxDate: moment().toDate(),
            }}
          />
        </div>
      </FilterSection>
      <div className={styles.divider} />
      {masterVisibilitySwitch !== FeedVisibility.User ? (
        <FilterSection
          title="Posted By"
          activeKey={activeFilterSection}
          setActiveSection={setActiveFilterSection}
        >
          <Autocomplete
            ddOptions={
              masterVisibilitySwitch === FeedVisibility.Team
                ? managedTeamMembers
                    .filter((member) => member.id !== currentUserId)
                    .map((item) => ({
                      value: item.id,
                      label: item.name,
                    }))
                : [
                    ...managedTeamMembers.map((item) => ({
                      value: item.id,
                      label: item.name,
                    })),
                    ...(agentRole === TEAM_ADMIN
                      ? [
                          {
                            value: currentUser.id,
                            label: currentUser.name,
                          },
                        ]
                      : []),
                  ]
            }
            placeholder="Search by name..."
            handleOptionsSelection={handleAgentSelection}
            useOptionsValues={
              formik.values.agents.map((item) => item.value).filter(Boolean) as number[]
            }
            allowCreation={false}
          />

          {formik.values.agents.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                width: '100%',
              }}
            >
              {formik.values.agents.map(({ value, label, active }, index) => (
                <Checkbox
                  key={value}
                  className={styles.checkbox}
                  labelClassName={styles.checkboxLabel}
                  labelTextClassName={styles.checkboxLabelText}
                  label={label}
                  checked={active}
                  onChange={() => {
                    formik.setFieldValue(
                      'agents',
                      [...formik.values.agents].map((l, i) =>
                        i === index ? { ...l, active: !l.active } : l,
                      ),
                    );
                  }}
                  closeIcon
                  onClickClose={() => {
                    formik.setFieldValue(
                      'agents',
                      [...formik.values.agents].filter((l, i) => i !== index),
                    );
                  }}
                  closeIconClassName={styles.closeIcon}
                  direction={Checkbox.DIRECTION_RIGHT}
                />
              ))}
            </div>
          )}
        </FilterSection>
      ) : (
        <FilterSection
          title="Tags"
          count={activeTagsCount}
          activeKey={activeFilterSection}
          setActiveSection={setActiveFilterSection}
        >
          <Autocomplete
            ddOptions={systemTags.map((item) => ({
              value: item.Id,
              label: item.Title,
            }))}
            placeholder="Search by tags..."
            handleOptionsSelection={handleTagSelection}
            useOptionsValues={
              formik.values.tags.map((item) => item.value).filter(Boolean) as number[]
            }
            allowCreation={false}
          />
          <div className={styles.tagWrapper}>
            {formik.values.tags.map((tag, index) => (
              <div
                className={classNames(styles.tag, {
                  [styles.active]: tag.active,
                })}
                key={`${tag.label}-${index}`}
                onClick={() => {
                  const nextTags = formik.values.tags.map((t) =>
                    t.label === tag.label ? { ...t, active: !t.active } : t,
                  );

                  return formik.setFieldValue('tags', nextTags);
                }}
              >
                <div>{tag.label}</div>
              </div>
            ))}
          </div>
        </FilterSection>
      )}
    </div>
  );
};
